import React, { useEffect, useState } from "react";
import { Clock, Package, FileText, Building2, CheckCircle2, XCircle, Truck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BuyerApprovalBanner from "@/components/BuyerApprovalBanner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";

const statusColor = {
  placed: "bg-slate-100 text-slate-700",
  confirmed: "bg-yellow-100 text-slate-800",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-emerald-100 text-emerald-700",
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  on_invoice: "bg-purple-100 text-purple-700",
  open: "bg-amber-100 text-amber-700",
  responded: "bg-emerald-100 text-emerald-700",
};

export default function BuyerDashboard() {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    refresh();
    api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
    api.get("/quotes").then((r) => setQuotes(r.data.quotes)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <BuyerApprovalBanner />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-[#FFDA00]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">{user?.company_name}</h1>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              {user?.contact_person} ·
              <Badge className={`${statusColor[user?.status] || "bg-slate-100 text-slate-700"} border-0 capitalize`} data-testid="account-status-badge">
                {user?.status === "approved" ? "Verified buyer" : user?.status === "needs_review" ? "pending" : user?.status}
              </Badge>
            </div>
          </div>
        </div>

        <Tabs defaultValue="orders">
          <TabsList>
            <TabsTrigger value="orders" data-testid="tab-orders"><Package className="h-4 w-4 mr-1.5" />Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes"><FileText className="h-4 w-4 mr-1.5" />Quotes ({quotes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-6">
            {orders.length === 0 ? (
              <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-200">No orders yet.</div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`order-row-${o.order_number}`} className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-mono-num font-semibold text-slate-900">{o.order_number}</div>
                        <div className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${statusColor[o.status] || "bg-slate-100"} border-0 capitalize`}>{o.status}</Badge>
                        <Badge className={`${statusColor[o.payment_status] || "bg-slate-100"} border-0 capitalize`}>{(o.payment_status || "").replace("_", " ")}</Badge>
                        <span className="font-mono-num font-bold text-slate-900">{formatINR(o.total)}</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-500">
                      {o.items.map((i) => `${i.name} ×${i.quantity}`).join("  ·  ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="quotes" className="mt-6">
            {quotes.length === 0 ? (
              <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-200">No quote requests yet.</div>
            ) : (
              <div className="space-y-4">
                {quotes.map((q) => (
                  <div key={q.id} data-testid={`quote-row-${q.id}`} className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-400">{new Date(q.created_at).toLocaleString()}</div>
                      <Badge className={`${statusColor[q.status] || "bg-slate-100"} border-0 capitalize`}>{q.status}</Badge>
                    </div>
                    <p className="mt-2 text-slate-700">{q.message}</p>
                    {q.admin_response && (
                      <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                        <div className="text-xs font-semibold text-emerald-700 mb-1">Our response</div>
                        <p className="text-sm text-slate-700">{q.admin_response}</p>
                        {q.quoted_total != null && <div className="mt-1 font-mono-num font-bold text-emerald-700">{formatINR(q.quoted_total)}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
